import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type FormData = {
  bookBusiness: string;
  generatingIncome: string;
  revenueRange: string;
  projectType: string;
  name: string;
  email: string;
};

const revenueOptions = [
  'Under $2,000/month',
  '$2,000 – $5,000/month',
  '$5,000 – $10,000/month',
  '$10,000 – $25,000/month',
  '$25,000+/month',
];

const projectTypeOptions = [
  'Non-fiction / Business book',
  'Memoir or personal story',
  'Self-help / Personal development',
  'Professional guide or manual',
  'Children\'s or illustrated book',
  'Other',
];

export default function AuthorFundingPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    bookBusiness: '',
    generatingIncome: '',
    revenueRange: '',
    projectType: '',
    name: '',
    email: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isValid = () =>
    formData.bookBusiness &&
    formData.generatingIncome &&
    formData.revenueRange &&
    formData.projectType &&
    formData.name.trim() &&
    formData.email.trim();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid()) return;
    setSubmitting(true);
    setError('');

    const body = new URLSearchParams();
    body.append('name', formData.name);
    body.append('email', formData.email);
    body.append('Is your book connected to a business, coaching program, or professional brand?', formData.bookBusiness);
    body.append('Are you currently generating income related to your expertise or audience?', formData.generatingIncome);
    body.append('What is your approximate monthly revenue range?', formData.revenueRange);
    body.append('What type of publishing project are you planning?', formData.projectType);

    try {
      await fetch('https://readdy.ai/api/form/d6t4l7a4of3kuoicmuq0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Sticky Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-8 py-5 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <img
              src="https://public.readdy.ai/ai/img_res/694cee17-fcfc-433e-9858-ffd49fe7e172.png"
              alt="Daily Nest Publishing"
              className="h-10 w-auto"
            />
            <span className="text-white text-lg font-semibold group-hover:text-[#D4AF37] transition-colors">Daily Nest Publishing</span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-[#D4AF37] transition-colors text-sm font-medium cursor-pointer"
          >
            <i className="ri-arrow-left-line text-base"></i>
            Back to Home
          </button>
        </div>
      </nav>

      {/* Hero Banner */}
      <div className="pt-32 pb-20 px-8 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 via-transparent to-transparent"></div>
        <div className="relative max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-medium px-5 py-2 rounded-full mb-10">
            <i className="ri-funds-line text-sm"></i>
            Author Funding Options
          </div>
          <h1 className="text-5xl md:text-6xl font-bold leading-[1.1] mb-8 text-white">
            Explore Author<br />
            <span className="text-[#D4AF37]">Funding Options</span>
          </h1>
          <p className="text-[#D9D9D9] text-xl leading-relaxed max-w-2xl mx-auto">
            Some authors qualify for funding solutions that can help support their publishing and marketing investment.
          </p>
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent mx-8"></div>

      {/* Who Qualifies */}
      <div className="py-20 px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Who May Qualify</h2>
              <p className="text-gray-400 text-base leading-relaxed mb-10">
                Funding may be available for authors who are building real businesses around their expertise. If your book is a strategic asset rather than a passion project, you may qualify for options that separate the publishing investment from your personal cash flow.
              </p>
              <div className="space-y-5">
                {[
                  { icon: 'ri-building-2-line', text: 'Are building a business around their book' },
                  { icon: 'ri-mic-2-line', text: 'Generate income from consulting, coaching, or speaking' },
                  { icon: 'ri-group-line', text: 'Have an established audience or revenue stream' },
                ].map(({ icon, text }) => (
                  <div key={text} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex-shrink-0 mt-0.5">
                      <i className={`${icon} text-[#D4AF37] text-base`}></i>
                    </div>
                    <p className="text-[#D9D9D9] text-base leading-relaxed pt-2">{text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats block */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '60%', label: 'of qualifying authors cover costs with zero out-of-pocket upfront' },
                { value: '$0', label: 'initial cost for approved funding applicants' },
                { value: '3–5 days', label: 'average qualification review turnaround' },
                { value: '100%', label: 'confidential consultation, no obligation' },
              ].map(({ value, label }) => (
                <div key={value} className="bg-white/[0.03] border border-white/10 rounded-xl p-6">
                  <div className="text-[#D4AF37] text-3xl font-bold mb-2">{value}</div>
                  <div className="text-gray-400 text-sm leading-relaxed">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mx-8"></div>

      {/* Ways Authors May Qualify */}
      <div className="py-20 px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-white mb-4">Ways Authors May Qualify for Funding</h2>
            <p className="text-gray-400 text-base leading-relaxed max-w-xl mx-auto">
              There are two primary paths authors typically qualify through. Either path can fully cover publishing and marketing costs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-14">
            {/* Option 1 */}
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                  <i className="ri-line-chart-line text-[#D4AF37] text-base"></i>
                </div>
                <div>
                  <div className="text-[#D4AF37] text-xs uppercase tracking-[0.15em] font-medium">Option 1</div>
                  <div className="text-white font-bold text-lg leading-tight">Revenue-Based Funding</div>
                </div>
              </div>

              <p className="text-gray-400 text-sm leading-relaxed mb-7">
                For authors who already have a business or income connected to their expertise.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  'Coaches and consultants',
                  'Business owners writing authority books',
                  'Professionals building a personal brand',
                  'Authors generating speaking, course, or consulting income',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#D4AF37] text-sm"></i>
                    </div>
                    <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 pt-6">
                <p className="text-gray-500 text-xs leading-relaxed italic">
                  "Many business authors use funding to accelerate publishing, marketing, and distribution of their book."
                </p>
              </div>
            </div>

            {/* Option 2 */}
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                  <i className="ri-shield-star-line text-[#D4AF37] text-base"></i>
                </div>
                <div>
                  <div className="text-[#D4AF37] text-xs uppercase tracking-[0.15em] font-medium">Option 2</div>
                  <div className="text-white font-bold text-lg leading-tight">Credit-Based Funding</div>
                </div>
              </div>

              <p className="text-gray-400 text-sm leading-relaxed mb-7">
                Some authors may qualify based on personal credit strength rather than existing business income.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  'Have strong personal credit',
                  'Prefer flexible financing options',
                  'Want to invest in their book launch and marketing strategy',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#D4AF37] text-sm"></i>
                    </div>
                    <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 pt-6">
                <p className="text-gray-500 text-xs leading-relaxed italic">
                  "Credit-based options may allow qualified authors to fund their publishing project even if the book itself is not yet generating revenue."
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <button
              onClick={() => {
                const formSection = document.getElementById('qualification-form');
                if (formSection) formSection.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-[#D4AF37] text-black px-10 h-14 rounded-full font-semibold text-base hover:brightness-110 transition-all cursor-pointer whitespace-nowrap"
            >
              Check Funding Eligibility
            </button>
          </div>
        </div>
      </div>

      {/* Divider after Ways Authors May Qualify */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mx-8"></div>

      {/* ─── AUTHOR FUNDING ELIGIBILITY ─── */}
      <div className="py-24 px-8">
        <div className="max-w-5xl mx-auto">

          {/* Section Header */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-medium px-5 py-2 rounded-full mb-8">
              <i className="ri-award-line text-sm"></i>
              Eligibility Overview
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Author Funding Eligibility
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed max-w-2xl mx-auto">
              See how authors typically qualify to fund their book, brand, and marketing — without requiring full upfront capital.
            </p>
          </div>

          {/* Section 1 — How Funding Works */}
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-10 mb-6">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex-shrink-0">
                <i className="ri-lightbulb-flash-line text-[#D4AF37] text-lg"></i>
              </div>
              <div>
                <h3 className="text-white text-xl font-bold mb-4">How Funding Works</h3>
                <p className="text-gray-400 text-base leading-relaxed mb-2">
                  Authors typically qualify through one of two paths.
                </p>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Both options are designed to help you move forward with publishing and marketing without delaying your project due to upfront cost.
                </p>
              </div>
            </div>
          </div>

          {/* Section 2 — Two-Column Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Revenue Path */}
            <div className="group bg-white/[0.03] border border-white/10 hover:border-[#D4AF37]/30 rounded-2xl p-10 transition-all duration-300">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-semibold">Option 1</span>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <i className="ri-line-chart-line text-[#D4AF37] text-base"></i>
                </div>
                <h4 className="text-white font-bold text-lg">Revenue-Based Funding</h4>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-7">
                Designed for authors who already generate income from their expertise.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  'Coaches and consultants',
                  'Business owners',
                  'Professionals with active clients',
                  'Authors earning from services, speaking, or programs',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#D4AF37] text-sm"></i>
                    </div>
                    <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 pt-6">
                <p className="text-[#D4AF37]/80 text-xs font-medium uppercase tracking-widest">
                  Qualification is based on current income performance.
                </p>
              </div>
            </div>

            {/* Credit Path */}
            <div className="group bg-white/[0.03] border border-white/10 hover:border-[#D4AF37]/30 rounded-2xl p-10 transition-all duration-300">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-semibold">Option 2</span>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <i className="ri-shield-star-line text-[#D4AF37] text-base"></i>
                </div>
                <h4 className="text-white font-bold text-lg">Credit-Based Funding</h4>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-7">
                Designed for authors building a brand or business without existing income.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  'Strong personal credit profile',
                  'Early-stage or first-time authors',
                  'Professionals preparing to launch',
                  'Individuals investing in long-term growth',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#D4AF37] text-sm"></i>
                    </div>
                    <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 pt-6">
                <p className="text-[#D4AF37]/80 text-xs font-medium uppercase tracking-widest">
                  Qualification is based on personal credit strength.
                </p>
              </div>
            </div>
          </div>

          {/* Section 3 — What Funding Can Be Used For */}
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-10 mb-6">
            <h3 className="text-white text-xl font-bold mb-8">What Funding Can Be Used For</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { icon: 'ri-quill-pen-line', label: 'Book development and writing' },
                { icon: 'ri-edit-2-line', label: 'Editing and formatting' },
                { icon: 'ri-printer-line', label: 'Publishing and distribution' },
                { icon: 'ri-palette-line', label: 'Branding and positioning' },
                { icon: 'ri-rocket-2-line', label: 'Marketing and launch strategy' },
                { icon: 'ri-global-line', label: 'Platform and audience building' },
              ].map(({ icon, label }) => (
                <div key={label} className="flex items-center gap-3 bg-white/[0.03] rounded-xl px-5 py-4 border border-white/[0.06]">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${icon} text-[#D4AF37] text-base`}></i>
                  </div>
                  <span className="text-gray-300 text-sm leading-snug">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Section 4 — Who This Is Designed For */}
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-10 mb-6">
            <div className="grid md:grid-cols-2 gap-10 items-start">
              <div>
                <h3 className="text-white text-xl font-bold mb-4">Who This Is Designed For</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Funding is best suited for authors who are building something beyond the book itself.
                </p>
              </div>
              <div className="space-y-3">
                {[
                  'Creating a business or personal brand',
                  'Planning to generate income from their expertise',
                  'Looking to position themselves as an authority',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#D4AF37] text-sm"></i>
                    </div>
                    <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
                <div className="pt-4 border-t border-white/10 mt-4">
                  <p className="text-gray-500 text-xs leading-relaxed italic">
                    This is not typically intended for casual or hobby-based publishing.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 5 — What to Expect */}
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-10 mb-14">
            <h3 className="text-white text-xl font-bold mb-8">What to Expect</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { step: '01', icon: 'ri-timer-line', title: 'Quick Eligibility Check', desc: '2–3 minutes to complete' },
                { step: '02', icon: 'ri-search-eye-line', title: 'Review & Funding Match', desc: 'Results within 3–5 business days' },
                { step: '03', icon: 'ri-compass-3-line', title: 'Clear Guidance', desc: 'Your best available option, explained simply' },
              ].map(({ step, icon, title, desc }) => (
                <div key={step} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex-shrink-0">
                    <i className={`${icon} text-[#D4AF37] text-base`}></i>
                  </div>
                  <div>
                    <div className="text-[#D4AF37] text-xs uppercase tracking-widest font-medium mb-1">Step {step}</div>
                    <div className="text-white text-sm font-semibold mb-1">{title}</div>
                    <div className="text-gray-500 text-xs leading-relaxed">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 6 — CTA */}
          <div className="text-center">
            <button
              onClick={() => {
                const formSection = document.getElementById('qualification-form');
                if (formSection) formSection.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-[#D4AF37] text-black px-12 h-14 rounded-full font-semibold text-base hover:brightness-110 transition-all cursor-pointer whitespace-nowrap mb-4"
            >
              See If You Qualify (2-Min Check)
            </button>
            <p className="text-gray-500 text-sm">No obligation. Confidential review.</p>
          </div>

          {/* Micro-proof strip */}
          <div className="mt-10 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl px-8 py-5 text-center">
            <p className="text-gray-400 text-sm leading-relaxed">
              <i className="ri-double-quotes-l text-[#D4AF37] mr-1"></i>
              Many qualified authors are able to move forward without large upfront capital.
              <i className="ri-double-quotes-r text-[#D4AF37] ml-1"></i>
            </p>
          </div>

        </div>
      </div>

      {/* Divider before form */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mx-8"></div>

      {/* Qualification Form */}
      <div id="qualification-form" className="py-24 px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-4">Check Your Qualification</h2>
            <p className="text-gray-400 text-lg leading-relaxed">
              Answer four quick questions and we'll reach out to discuss your options — no commitment required.
            </p>
          </div>

          {submitted ? (
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-2xl p-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 mx-auto mb-6">
                <i className="ri-check-line text-[#D4AF37] text-3xl"></i>
              </div>
              <h3 className="text-white text-2xl font-bold mb-3">Request Received</h3>
              <p className="text-gray-400 text-base leading-relaxed mb-8">
                Thank you! Our team will review your submission and reach out within 1–2 business days to discuss your funding options.
              </p>
              <button
                onClick={() => navigate('/')}
                className="bg-[#D4AF37] text-black px-8 py-3 rounded-full font-medium hover:brightness-110 transition-all cursor-pointer whitespace-nowrap"
              >
                Return to Home
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-white/[0.03] border border-white/10 rounded-2xl p-10 space-y-10"
            >
              {/* Q1 */}
              <div>
                <label className="block text-white font-medium text-sm mb-4 leading-relaxed">
                  <span className="text-[#D4AF37] font-bold mr-2">01.</span>
                  Is your book connected to a business, coaching program, or professional brand?
                </label>
                <div className="flex gap-4">
                  {['Yes', 'No', 'Not yet, but planning to'].map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => handleChange('bookBusiness', opt)}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all cursor-pointer whitespace-nowrap ${
                        formData.bookBusiness === opt
                          ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
                          : 'bg-transparent border-white/20 text-gray-300 hover:border-[#D4AF37]/50 hover:text-white'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Q2 */}
              <div>
                <label className="block text-white font-medium text-sm mb-4 leading-relaxed">
                  <span className="text-[#D4AF37] font-bold mr-2">02.</span>
                  Are you currently generating income related to your expertise or audience?
                </label>
                <div className="flex gap-4">
                  {['Yes', 'No', 'Occasionally'].map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => handleChange('generatingIncome', opt)}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all cursor-pointer whitespace-nowrap ${
                        formData.generatingIncome === opt
                          ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
                          : 'bg-transparent border-white/20 text-gray-300 hover:border-[#D4AF37]/50 hover:text-white'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Q3 */}
              <div>
                <label className="block text-white font-medium text-sm mb-4 leading-relaxed">
                  <span className="text-[#D4AF37] font-bold mr-2">03.</span>
                  What is your approximate monthly revenue range?
                </label>
                <div className="flex flex-wrap gap-3">
                  {revenueOptions.map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => handleChange('revenueRange', opt)}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all cursor-pointer whitespace-nowrap ${
                        formData.revenueRange === opt
                          ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
                          : 'bg-transparent border-white/20 text-gray-300 hover:border-[#D4AF37]/50 hover:text-white'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Q4 */}
              <div>
                <label className="block text-white font-medium text-sm mb-4 leading-relaxed">
                  <span className="text-[#D4AF37] font-bold mr-2">04.</span>
                  What type of publishing project are you planning?
                </label>
                <div className="flex flex-wrap gap-3">
                  {projectTypeOptions.map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => handleChange('projectType', opt)}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all cursor-pointer whitespace-nowrap ${
                        formData.projectType === opt
                          ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
                          : 'bg-transparent border-white/20 text-gray-300 hover:border-[#D4AF37]/50 hover:text-white'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact */}
              <div className="border-t border-white/10 pt-10 space-y-5">
                <p className="text-gray-400 text-sm">
                  Where should we send your consultation details?
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-xs mb-2 uppercase tracking-widest">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={e => handleChange('name', e.target.value)}
                      placeholder="Your full name"
                      className="w-full bg-white/[0.05] border border-white/15 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-2 uppercase tracking-widest">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={e => handleChange('email', e.target.value)}
                      placeholder="your@email.com"
                      className="w-full bg-white/[0.05] border border-white/15 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                    />
                  </div>
                </div>
              </div>

              {error && (
                <p className="text-red-400 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={!isValid() || submitting}
                className="w-full bg-[#D4AF37] text-black py-4 rounded-full font-semibold text-base hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {submitting ? 'Submitting...' : 'Request Funding Consultation'}
              </button>

              <p className="text-gray-600 text-xs text-center leading-relaxed">
                By submitting, you agree to be contacted by our team. This is a consultation request, not a loan application. No credit check required.
              </p>
            </form>
          )}
        </div>
      </div>

      {/* Footer strip */}
      <div className="border-t border-white/10 py-8 px-8 text-center">
        <p className="text-gray-600 text-sm">
          &copy; {new Date().getFullYear()} Daily Nest Publishing. All rights reserved.
        </p>
      </div>
    </div>
  );
}
